import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import moment from 'moment';
import {
  MortgageType,
  RealEstateDTO,
  RealEstateInvestmentType,
} from '../../tables/RealEstate/RealEstate.meta';
import {
  dataToForm,
  DATE_FE_FORMAT,
  formToBody,
  REAL_ESTATE_FORM_SCHEMA,
  RealEstateFormValues,
  UpsertRealEstateDTO,
} from './RealEstateForm.meta';
import { useUpsertRealEstateMutation } from '../../api/useUpsertRealEstateMutation';
import { toast } from '../../../../CommonComponents/OPL/Toast/Toast';
import { InstitutionOption } from '../../components/SearchInstitutionsSelect/useSearchInstitutionsSelect';
import {
  ADD_ASSET_SUCCESS_MESSAGE,
  CLONE_ASSET_SUCCESS_MESSAGE,
  EDIT_ASSET_SUCCESS_MESSAGE,
} from '../../OtherAssets.meta';
import { OtherAssetsDTO } from '../../api/useOtherAssetsQuery';
import {
  EXCLUDE_OPT,
  getAssetBucketOptions,
} from '../components/AssetBucketSelect/AssetBucketSelect.meta';
import { onManagementSelect as baseOnManagementSelect } from '../components/ManagementStyle/ManagementStyle.meta';
import { selectCurrentClient } from '../../../../CommonSelectors/CommonAreaSelector';

const FIELDS_TO_SHOW_ADVANCED = [
  'SaleYear',
  'SaleNetEarnings',
  'MortgageMonthlyPmi',
  'MonthlyHoa',
  'PropertyTaxPerYear',
  'HomeInsurancePerYear',
  'IsSoldAtRetirement',
  'IncomeGrowth',
];

interface Params {
  clientId: string;
  asset: RealEstateDTO | null;
  options: OtherAssetsDTO['Options'];
  onSuccess: () => void;
}

export const useRealEstateForm = ({ clientId, asset, options, onSuccess }: Params) => {
  const formBag = useForm<RealEstateFormValues>({
    defaultValues: dataToForm({ data: asset, options }),
    resolver: yupResolver(REAL_ESTATE_FORM_SCHEMA),
  });

  const [mortgageInstitution, setMortgageInstitution] = useState<InstitutionOption | null>(
    asset?.MortgageInstitutionId && asset.MortgageInstitutionName
      ? {
          label: asset.MortgageInstitutionName,
          value: asset.MortgageInstitutionId,
        }
      : null,
  );

  const { Dob: primaryDob, PartnerDob: spouseDob } = useSelector(selectCurrentClient);

  const primaryYob = primaryDob ? new Date(primaryDob).getFullYear() : null;
  const spouseYob = spouseDob ? new Date(spouseDob).getFullYear() : null;

  const ownerModel = formBag.watch('ownerModel');

  const mutationBag = useUpsertRealEstateMutation({ clientId });

  const ownerOptions = [
    {
      label: 'People',
      options: options.RealEstateOptions.ownerOptions.People.map(owner => ({
        value: owner.Name,
        label: owner.Name,
      })),
    },
    {
      label: 'Trusts',
      options: options.RealEstateOptions.ownerOptions.Trusts.map(owner => ({
        value: owner.Name,
        label: owner.Name,
      })),
    },
  ];

  const mortgageTypeOptions = [
    {
      label: 'No Mortgage',
      value: null,
    },
    {
      label: 'Link Existing Mortgage',
      value: 'Existing',
    },
    ...options.RealEstateOptions.mortgageTypeOptions,
  ];

  const isPrimary =
    ownerModel.length === 1 &&
    ownerModel[0].Name === options.RealEstateOptions.ownerOptions.People[0].Name;
  const isSpouse =
    ownerModel.length === 1 &&
    ownerModel[0].Name === options.RealEstateOptions.ownerOptions.People[1]?.Name;
  const isJoint = ownerModel.length > 1 || ownerModel.some(owner => owner.Name === 'Joint');

  const saleYearOptions = options.RealEstateOptions.SaleYearOptions.map(year => {
    const yearNumber = Number(year);

    let rightLabel: string | undefined;

    if (Number.isInteger(yearNumber)) {
      const getAgeLabel = (birthYear: number) => `Age ${yearNumber - birthYear}`;

      if (isJoint && primaryYob) {
        rightLabel = `${getAgeLabel(primaryYob)}${spouseYob ? `, ${getAgeLabel(spouseYob)}` : ''}`;
      } else if (isPrimary && primaryYob) {
        rightLabel = getAgeLabel(primaryYob);
      } else if (isSpouse && spouseYob) {
        rightLabel = getAgeLabel(spouseYob);
      }
    }

    return {
      value: year,
      label: year,
      rightLabel,
    };
  });

  const [managementStyleSelection, mortgageType, investmentType] = formBag.watch([
    'orionManagementStyleId',
    'mortgageType',
    'investmentType',
  ]);

  const showMortgageSelect = mortgageType === 'Existing';

  const showMortgageFields =
    mortgageType !== null && mortgageType.toString() !== '' && !showMortgageSelect;

  const showMonthlyPayment = showMortgageFields && mortgageType !== MortgageType.InterestOnly;

  const mortgageOptions = options.RealEstateOptions.ExistingMortgages.map(mortgage => ({
    label: mortgage.MortgageLabel,
    value: mortgage.MortgageId,
  }));

  const defaultShowAdvanced =
    !!asset && FIELDS_TO_SHOW_ADVANCED.some(field => !!asset[field] || asset[field] === 0);

  const linkExistingMortgage = (mortgageId?: string) => {
    const selectedMortgage = options.RealEstateOptions.ExistingMortgages.find(
      mortgage => mortgage.MortgageId === mortgageId,
    );

    if (selectedMortgage) {
      formBag.setValue('mortgageType', selectedMortgage.MortgageType);
      formBag.setValue('mortgageInterestRate', selectedMortgage.MortgageInterestRate);
      formBag.setValue(
        'mortgageOriginationDate',
        selectedMortgage.MortgageOriginationDate
          ? moment(selectedMortgage.MortgageOriginationDate).format(DATE_FE_FORMAT)
          : null,
        { shouldValidate: true },
      );
      formBag.setValue('mortgageBalance', selectedMortgage.MortgageOutstandingBalance, {
        shouldValidate: true,
      });
      formBag.setValue('mortgageMonthlyPayment', selectedMortgage.MortgageMonthlyPayment);
      formBag.setValue('mortgageMonthlyPmi', selectedMortgage.MortgageMonthlyPmi);
      formBag.setValue(
        'mortgageExcludeFromDebtManagementGoal',
        selectedMortgage.MortgageExcludeFromDebtManagementGoal,
      );

      setMortgageInstitution({
        value: selectedMortgage.MortgageInstitutionId,
        label: selectedMortgage.MortgageInstitutionName,
      });
    }
  };

  const defaultBucketIndex =
    investmentType === null
      ? null
      : investmentType === RealEstateInvestmentType.Personal
      ? options.RealEstateOptions.DefaultAssetBucketIndices.Personal
      : options.RealEstateOptions.DefaultAssetBucketIndices.Investment;

  const baseAssetBucketOptions = [
    ...options.AvailableAssetBuckets.map(({ Title }, index) => ({
      label: Title,
      value: index,
      rightLabel: index === defaultBucketIndex ? 'Default' : undefined,
    })),
    EXCLUDE_OPT,
  ];

  const onSubmit = formBag.handleSubmit(async (values: RealEstateFormValues) => {
    const body: UpsertRealEstateDTO = {
      clientId,
      ...formToBody(values),
      Id: (!asset?.isClone && asset?.Id) || null,
      MortgageId: (!asset?.isClone && asset?.MortgageId) || null,
      MortgageInstitutionId: mortgageInstitution?.value || null,
      MortgageInstitutionName: mortgageInstitution?.label || null,
    };

    try {
      await mutationBag.mutateAsync(body);

      toast.success(
        asset?.Id
          ? asset?.isClone
            ? CLONE_ASSET_SUCCESS_MESSAGE
            : EDIT_ASSET_SUCCESS_MESSAGE
          : ADD_ASSET_SUCCESS_MESSAGE,
      );

      onSuccess();
    } catch (error) {
      console.error(error);
    }
  });

  const {
    AssetBucketingByManagementStyle: assetBucketingByManagementStyle,
    ManagementStyles,
    DisplayAssetBucketing: displayAssetBucketing,
  } = options;

  const managementStylesOptions = ManagementStyles.map(option => ({
    label: option.name,
    value: option.id,
    defaultBucketIndex: option.defaultBucket,
  }));

  const assetBucketOptions = getAssetBucketOptions({
    assetBucketOptions: baseAssetBucketOptions,
    assetBucketingByManagementStyle,
    managementStyleSelection,
    managementStylesOptions,
  });

  const onManagementSelect = baseOnManagementSelect({
    options: managementStylesOptions,
    setValue: (value: number) => formBag.setValue('customBucketIndex', value),
  });

  const resetValues = () => {
    formBag.reset({ ...dataToForm({ data: asset, options }) });
  };

  return {
    data: {
      formBag,
      mutationBag,
      ownerOptions,
      mortgageTypeOptions,
      saleYearOptions,
      showMortgageSelect,
      showMortgageFields,
      showMonthlyPayment,
      mortgageOptions,
      mortgageInstitution,
      defaultShowAdvanced,
      assetBucketOptions,
      managementStylesOptions,
      onManagementSelect,
      assetBucketingByManagementStyle,
      allowAssetBucketingCustomization: options.AllowAssetBucketingCustomization,
      displayAssetBucketing,
    },

    actions: {
      onSubmit,
      linkExistingMortgage,
      setMortgageInstitution,
      resetValues,
    },
  };
};
